import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, BookOpen, FileText, Clock, Target } from "lucide-react";

const jhsSubjectNames = {
  mathematics: "Mathematics",
  "english-language": "English Language",
  "integrated-science": "Integrated Science",
  "social-studies": "Social Studies",
  rme: "Religious & Moral Education",
  "creative-arts": "Creative Arts",
  "career-technology": "Career Technology",
  french: "French",
  fante: "Fante",
  ict: "ICT"
};

export const JHSExamPrepPage = () => {
  const navigate = useNavigate();
  const { subject, prepId } = useParams();
  const subjectName = jhsSubjectNames[subject] || subject;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#002147] to-[#003366]" data-testid={`exam-prep-${subject}-${prepId}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#002147] via-[#003366] to-[#002147] py-8">
        <div className="max-w-5xl mx-auto px-4">
          <Button 
            className="mb-4 bg-gradient-to-b from-[#FF6B00] to-[#e65c00] hover:from-[#e65c00] hover:to-[#FF6B00] text-white rounded-full shadow-[0_4px_0_0_#b34700] hover:shadow-[0_2px_0_0_#b34700] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium px-6"
            onClick={() => navigate(`/jhs-subject/${subject}`)}
            data-testid="back-to-subject-btn"
          >
            <ArrowLeft size={16} className="mr-2" /> Back to {subjectName}
          </Button>
          <div className="text-center">
            <div className="inline-block px-4 py-1 bg-[#FF6B00] text-white text-sm font-semibold rounded-full mb-3">
              JHS - {subjectName}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              Exams Prep {prepId}
            </h1>
            <p className="text-gray-300 text-lg">
              Prepare for your BECE and school exams with practice materials
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <BookOpen size={20} /> Exam Preparation Module {prepId}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  This exam preparation module covers key topics and practice questions for {subjectName}. 
                  Work through the materials below to strengthen your understanding and improve your exam performance.
                </p>
                <div className="bg-blue-50 border-l-4 border-[#002147] p-4 rounded">
                  <p className="text-sm text-[#002147] font-medium">
                    Practice materials and exam questions will be uploaded here by your teacher. Check back regularly for updates.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <FileText size={20} /> Practice Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {["Section A: Objective Questions", "Section B: Theory Questions", "Section C: Practical/Applied Questions"].map((section, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="w-8 h-8 bg-[#002147] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-bold">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{section}</p>
                        <p className="text-xs text-gray-500">Coming soon - Check back for updates</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#002147]">
                  <Target size={20} /> Study Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {[
                    "Review your class notes and textbook chapters thoroughly",
                    "Practice past BECE questions for this subject",
                    "Form study groups with classmates",
                    "Ask your teacher about areas you find difficult",
                    "Create summary notes for quick revision"
                  ].map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-[#FF6B00] font-bold mt-0.5">-</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Other Exam Preps */}
          <div className="space-y-6">
            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-[#002147] text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock size={18} /> Exam Info
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Subject</p>
                  <p className="font-semibold text-[#002147]">{subjectName}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Module</p>
                  <p className="font-semibold text-[#002147]">Prep {prepId} of 15</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Level</p>
                  <p className="font-semibold text-[#002147]">JHS (BECE Prep)</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#FF6B00] to-[#e65c00] text-white rounded-t-lg">
                <CardTitle className="text-lg text-center">All Exam Preps</CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 15 }, (_, i) => {
                    const isActive = parseInt(prepId) === i + 1;
                    const prepColors = [
                      { bg: 'linear-gradient(145deg, #ff6b6b, #ee5a24)', shadow: '#c0392b' },
                      { bg: 'linear-gradient(145deg, #ffa502, #e67e22)', shadow: '#d35400' },
                      { bg: 'linear-gradient(145deg, #7bed9f, #2ed573)', shadow: '#27ae60' },
                      { bg: 'linear-gradient(145deg, #70a1ff, #1e90ff)', shadow: '#0c5bb5' },
                      { bg: 'linear-gradient(145deg, #a29bfe, #6c5ce7)', shadow: '#4834d4' },
                      { bg: 'linear-gradient(145deg, #fd79a8, #e84393)', shadow: '#b83280' },
                      { bg: 'linear-gradient(145deg, #fdcb6e, #f39c12)', shadow: '#d68910' },
                      { bg: 'linear-gradient(145deg, #55efc4, #00b894)', shadow: '#00856a' },
                      { bg: 'linear-gradient(145deg, #74b9ff, #0984e3)', shadow: '#0767b1' },
                      { bg: 'linear-gradient(145deg, #dfe6e9, #b2bec3)', shadow: '#808e95' },
                      { bg: 'linear-gradient(145deg, #e17055, #d63031)', shadow: '#b71c1c' },
                      { bg: 'linear-gradient(145deg, #00cec9, #0097a7)', shadow: '#00796b' },
                      { bg: 'linear-gradient(145deg, #6c5ce7, #5f27cd)', shadow: '#4a148c' },
                      { bg: 'linear-gradient(145deg, #fab1a0, #e17055)', shadow: '#c0392b' },
                      { bg: 'linear-gradient(145deg, #81ecec, #00b894)', shadow: '#00796b' },
                    ];
                    const colorSet = prepColors[i];
                    return (
                      <a
                        key={i}
                        href={`/jhs-subject/${subject}/exam-prep/${i + 1}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/jhs-subject/${subject}/exam-prep/${i + 1}`); }}
                        className={`block w-full text-center px-3 py-2 text-xs font-bold text-white rounded-lg cursor-pointer transition-all duration-200 hover:brightness-110 no-underline ${isActive ? 'ring-2 ring-white ring-offset-2' : ''}`}
                        style={{
                          background: colorSet.bg,
                          boxShadow: isActive ? `0 2px 0 ${colorSet.shadow}, 0 3px 5px rgba(0,0,0,0.15)` : `0 4px 0 ${colorSet.shadow}, 0 6px 10px rgba(0,0,0,0.2)`,
                          transform: isActive ? 'translateY(0px)' : 'translateY(-2px)',
                        }}
                      >
                        Exams Prep {i + 1} {isActive ? '(Current)' : ''}
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Back Button */}
        <div className="mt-8 text-center">
          <Button 
            className="bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] font-medium px-8"
            onClick={() => navigate(`/jhs-subject/${subject}`)}
            data-testid="back-to-subject-btn-bottom"
          >
            <ArrowLeft size={18} className="mr-2" /> Back to {subjectName}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JHSExamPrepPage;
